import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Check } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

/**
 * CreatorChip — avatar + handle + (optional) Follow button.
 * Used inside StyleFeed player, Home social rail, PDP reel cards, Creator page.
 */
export default function CreatorChip({ creator, showFollow = false, size = "md", onFollowChange }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(Boolean(creator?.is_following));
  const [busy, setBusy] = useState(false);

  if (!creator) return null;

  const toggleFollow = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user) {
      toast.error("Sign in to follow creators");
      return;
    }
    const next = !following;
    setBusy(true);
    try {
      if (next) {
        await api.post(`/creators/${creator.handle}/follow`);
      } else {
        await api.delete(`/creators/${creator.handle}/follow`);
      }
      setFollowing(next);
      onFollowChange?.(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update follow");
    } finally {
      setBusy(false);
    }
  };

  const avatarSize = size === "lg" ? "w-14 h-14" : size === "sm" ? "w-7 h-7" : "w-10 h-10";
  const textSize = size === "lg" ? "text-base" : size === "sm" ? "text-xs" : "text-sm";

  return (
    <div className={`flex items-center gap-2 ${size === "lg" ? "gap-3" : ""}`}>
      <Link
        to={`/creator/${creator.handle}`}
        className="flex items-center gap-2 group"
        onClick={(e) => e.stopPropagation()}
        data-testid={`creator-chip-${creator.handle}`}
      >
        {creator.avatar_url ? (
          <img
            src={creator.avatar_url}
            alt={creator.display_name}
            className={`${avatarSize} rounded-full object-cover bg-neutral-200 shrink-0 ring-1 ring-[hsl(var(--kb-border))]`}
            loading="lazy"
          />
        ) : (
          <div className={`${avatarSize} rounded-full bg-[hsl(var(--kb-surface-alt))] shrink-0`} />
        )}
        <div className="min-w-0">
          <div className={`${textSize} font-medium truncate flex items-center gap-1`}>
            {creator.display_name}
            {creator.is_verified && (
              <BadgeCheck size={size === "lg" ? 16 : 12} className="text-[hsl(var(--kb-primary))] shrink-0" />
            )}
          </div>
          <div className={`${size === "lg" ? "text-xs" : "text-[11px]"} text-[hsl(var(--kb-muted))] truncate`}>
            @{creator.handle}
          </div>
        </div>
      </Link>
      {showFollow && (
        <button
          onClick={toggleFollow}
          disabled={busy}
          className={`ml-auto shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
            following
              ? "bg-[hsl(var(--kb-surface-alt))] text-[hsl(var(--kb-text))] border-[hsl(var(--kb-border))]"
              : "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]"
          }`}
          data-testid={`creator-follow-${creator.handle}`}
        >
          {following ? (<span className="flex items-center gap-1"><Check size={11} /> Following</span>) : "Follow"}
        </button>
      )}
    </div>
  );
}
